import React from "react";
import { useI18n } from "@/context/I18nContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Seo from "@/components/Seo";
import { pageTitle } from "@/lib/seo";
import { useSiteContent, scValue, useCmsItems } from "@/hooks/useSiteContent";

const Faq = () => {
  const { t, lang } = useI18n();
  const sc = useSiteContent();
  const pf = sc?.page_faq;
  const faqItems = useCmsItems("faq");
  const title = scValue(pf, "title", lang, t("faq.title"));
  const qv = (d, base) => (lang === "en" ? d[`${base}_en`] : d[`${base}_it`]) || d[`${base}_it`] || d[`${base}_en`] || "";
  const fallback = ["1", "2", "3", "4", "5"].map((n) => ({ id: n, question: t(`faq.q${n}`), answer: t(`faq.a${n}`) }));
  const paymentFaq = {
    id: "payment-security",
    question: lang === "en" ? "Is it safe to pay on LearnSphereHub?" : "È sicuro pagare su LearnSphereHub?",
    answer: lang === "en"
      ? "Payments are processed through Stripe, a platform specialised in handling online payments. After payment confirmation, LearnSphereHub records the order and makes the digital product available following the normal purchase procedure. Full card details are not handled directly by LearnSphereHub."
      : "I pagamenti vengono elaborati tramite Stripe, una piattaforma specializzata nella gestione dei pagamenti online. Dopo la conferma del pagamento, LearnSphereHub registra l'ordine e rende disponibile il prodotto digitale secondo la normale procedura di acquisto. LearnSphereHub non gestisce direttamente i dati completi della carta utilizzata per il pagamento.",
  };
  const baseList = faqItems.length ? faqItems.map((it) => ({ id: it.id, question: qv(it.data, "question"), answer: qv(it.data, "answer") })) : fallback;
  const list = [...baseList, paymentFaq];
  return (
    <div data-testid="faq-page">
      <Seo
        title={pageTitle(title)}
        description={lang === "en"
          ? "Answers to frequent questions about digital products, downloads, secure Stripe payments and LearnSphereHub Smart Services."
          : "Risposte alle domande frequenti su prodotti digitali, download, pagamenti sicuri con Stripe e Servizi Smart di LearnSphereHub."}
        path="/faq"
        type="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: list.map((it) => ({
            "@type": "Question",
            name: it.question,
            acceptedAnswer: { "@type": "Answer", text: it.answer },
          })),
        }}
      />
      <section className="bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-20">
          <h1 className="text-4xl md:text-5xl font-extrabold font-heading tracking-tight">{title}</h1>
        </div>
      </section>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <Accordion type="single" collapsible className="space-y-4">
          {list.map((it, i) => (
            <AccordionItem key={it.id} value={String(it.id)} data-testid={`faq-item-${i + 1}`}
              className="rounded-2xl border border-brand-navy/10 bg-white px-6">
              <AccordionTrigger className="text-left font-heading font-semibold text-brand-navy hover:no-underline">{it.question}</AccordionTrigger>
              <AccordionContent className="text-brand-navy/70 leading-relaxed">{it.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
};

export default Faq;
